/**
 * Portal v2 settings (`/portal/settings`).
 *
 * Two-pane layout: left nav (Profile / Notifications / Support) + right
 * pane content in CardV2. Mirrors dashboard's settings-v2 chrome adapted
 * for the smaller portal scope.
 *
 * Notification preferences (email alerts, weekly digest, alert threshold) are
 * editable and persist via PATCH /portal/settings.
 * The org-level support-access controls are not available for domain_viewer
 * sessions and are intentionally omitted.
 */
import { useState } from "react";
import { Bell, LifeBuoy, User } from "lucide-react";
import { useBranding } from "@/lib/branding-context";
import { SectionHeader } from "@/components/primitives";
import { cn } from "@/lib/utils";
import { ProfilePane } from "./portal-settings/profile-pane";
import { NotificationsPane } from "./portal-settings/notifications-pane";
import { SupportPane } from "./portal-settings/support-pane";

type PaneId = "profile" | "notifications" | "support";

const PANES: Array<{
  id: PaneId;
  label: string;
  icon: typeof User;
}> = [
  { id: "profile", label: "Profile", icon: User },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "support", label: "Support", icon: LifeBuoy },
];

export function PortalSettingsV2Page() {
  const { branding } = useBranding();
  const [pane, setPane] = useState<PaneId>("profile");
  const showSupport = !!(branding?.support_email || branding?.support_url);

  return (
    <div className="space-y-3.5">
      <SectionHeader
        title="Settings"
        subtitle="Manage your profile and notification preferences."
      />

      <div className="grid gap-3.5 md:grid-cols-[200px_1fr]">
        <nav
          className="flex flex-col gap-0.5"
          aria-label="Settings sections"
        >
          {PANES.filter((p) => p.id !== "support" || showSupport).map((p) => {
            const Icon = p.icon;
            const active = p.id === pane;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setPane(p.id)}
                className={cn(
                  "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-[12.5px] font-medium transition-colors",
                  active
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-foreground/85 hover:bg-secondary hover:text-foreground"
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {p.label}
              </button>
            );
          })}
        </nav>

        <div>
          {pane === "profile" && <ProfilePane />}
          {pane === "notifications" && <NotificationsPane />}
          {pane === "support" && showSupport && <SupportPane />}
        </div>
      </div>
    </div>
  );
}
